import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Globe, Building2, Users, CreditCard, ClipboardList, BarChart3 } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { useAuthStore } from '@/stores/authStore'

const widgets = [
  {
    title: 'Site Settings',
    description: 'Configure global site preferences',
    icon: Globe,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    action: 'Configure',
  },
  {
    title: 'Company Settings',
    description: 'Manage company profile and details',
    icon: Building2,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    action: 'Configure',
  },
  {
    title: 'Roles Editor',
    description: 'Define and manage user roles',
    icon: Users,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    action: 'Edit Roles',
  },
  {
    title: 'Billing',
    description: 'Manage subscriptions and payments',
    icon: CreditCard,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    action: 'Manage Billing',
  },
  {
    title: 'Program Settings',
    description: 'Configure program defaults and options',
    icon: ClipboardList,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    action: 'Configure',
  },
  {
    title: 'Reports Editor',
    description: 'Build and manage report templates',
    icon: BarChart3,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    action: 'Edit Reports',
  },
]

const WIDGET_ROUTES = {
  'Site Settings':     '/site-settings',
  'Company Settings':  '/company-settings',
  'Roles Editor':      '/roles-editor',
}

const PlatformSetup = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isSuperuser = user?.role?.id === 1 || user?.role?.name === 'Superuser'

  const visibleWidgets = isSuperuser ? widgets : widgets.filter(w => w.title !== 'Site Settings')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform Setup"
        subtitle="Configure and manage your platform settings"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleWidgets.map(({ title, description, icon: Icon, iconBg, iconColor, action }) => {
          const route = WIDGET_ROUTES[title]
          return (
            <Card
              key={title}
              className="cursor-pointer transition-colors hover:border-slate-300"
              onClick={() => route && navigate(route)}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`p-3 ${iconBg} rounded-lg`}>
                    <Icon className={`h-8 w-8 ${iconColor}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-600">{description}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-2">
                  {action}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default PlatformSetup
